/*
  Size guide popout. Builds on the theme's ModalDialog, which already closes on the X button, on a tap
  outside and on Esc, and keeps focus inside while open.

  On top of that it works with the table in the Shopify page:
  - the column (or row) of the size picked on the product page is highlighted
  - tapping a size in the table picks that size on the product page

  The size labels in the table must match the product's size option values (S, M, L, XL ...).
*/
if (!customElements.get('size-guide-modal')) {
  customElements.define(
    'size-guide-modal',
    class SizeGuideModal extends ModalDialog {
      constructor() {
        super();
        this.enhanced = false;
        this.groups = new Map();
        this.lastSizeButton = null;

        // After a size is picked from the table the product form moves focus to its own radio, outside this
        // popout, so the modal's own Esc listener would no longer hear the key. Listen on the document while open.
        this.onDocumentKeyup = (event) => {
          if (event.code.toUpperCase() === 'ESCAPE' && this.hasAttribute('open')) this.hide();
        };
      }

      connectedCallback() {
        // The modal moves itself to <body>. Drop a stale copy left behind by an earlier render of the same section.
        if (!this.moved) {
          document.querySelectorAll('size-guide-modal').forEach((other) => {
            if (other !== this && other.id === this.id) other.remove();
          });
        }
        super.connectedCallback();
      }

      show(opener) {
        this.enhanceTable();
        this.highlightSelectedSize();
        this.unsubscribeVariantChange?.();
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.highlightSelectedSize();
          // Runs before the product form moves focus to its radio, so wait a tick and take it back.
          if (this.lastSizeButton) setTimeout(() => this.hasAttribute('open') && this.lastSizeButton.focus(), 0);
        });
        document.addEventListener('keyup', this.onDocumentKeyup);
        super.show(opener);
      }

      hide() {
        this.unsubscribeVariantChange?.();
        this.unsubscribeVariantChange = null;
        this.lastSizeButton = null;
        document.removeEventListener('keyup', this.onDocumentKeyup);

        // Picking a size re-renders the variant picker, so the link that opened the popout may be gone.
        if (this.openedBy && !this.openedBy.isConnected) {
          this.openedBy = document.querySelector(`modal-opener[data-modal="#${this.id}"] button`);
        }
        super.hide();
      }

      get sizeOptionName() {
        return this.dataset.sizeOptionName;
      }

      get variantSelects() {
        return document.getElementById(`variant-selects-${this.dataset.productSection}`);
      }

      normalize(text) {
        return (text || '').replace(/\s+/g, '').toUpperCase();
      }

      // Radio inputs and <option> elements of the size option, as rendered by the variant picker.
      sizeOptionElements() {
        const selects = this.variantSelects;
        if (!selects) return [];
        return Array.from(selects.querySelectorAll('input[type="radio"], option')).filter(
          (element) => element.dataset.optionName === this.sizeOptionName
        );
      }

      selectedSizeValue() {
        const elements = this.sizeOptionElements();
        const radio = elements.find((element) => element.tagName === 'INPUT' && element.checked);
        if (radio) return radio.value;
        const option = elements.find((element) => element.tagName === 'OPTION' && element.parentElement.value === element.value);
        return option ? option.value : null;
      }

      selectSize(value) {
        const element = this.sizeOptionElements().find((item) => item.value === value);
        if (!element) return;

        if (element.tagName === 'INPUT') {
          if (!element.checked) element.click();
        } else {
          const select = element.parentElement;
          if (select.value !== value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        this.highlight(this.normalize(value));
      }

      // Every table in the page (a page may hold several, for example shirts and hoodies).
      enhanceTable() {
        if (this.enhanced) return;
        this.enhanced = true;

        const sizes = new Map(this.sizeOptionElements().map((element) => [this.normalize(element.value), element.value]));
        this.querySelectorAll('.size-guide-modal__body table').forEach((table) => {
          const wrap = document.createElement('div');
          wrap.className = 'size-guide-modal__table-wrap';
          table.parentNode.insertBefore(wrap, table);
          wrap.appendChild(table);
          this.enhanceOneTable(table, sizes);
        });
      }

      // Finds the size labels in one table: across the first row, or down the first column.
      enhanceOneTable(table, sizes) {
        const rows = Array.from(table.rows);
        if (!rows.length || !sizes.size) return;

        const headerCells = Array.from(rows[0].cells);
        const columnMatches = headerCells.filter((cell) => sizes.has(this.normalize(cell.textContent)));
        const firstColumn = rows.map((row) => row.cells[0]).filter(Boolean);
        const rowMatches = firstColumn.filter((cell) => sizes.has(this.normalize(cell.textContent)));

        if (columnMatches.length && columnMatches.length >= rowMatches.length) {
          columnMatches.forEach((cell) => {
            const index = headerCells.indexOf(cell);
            const cells = rows.map((row) => row.cells[index]).filter(Boolean);
            this.addGroup(cell, cells, sizes);
          });
        } else if (rowMatches.length) {
          rowMatches.forEach((cell) => {
            this.addGroup(cell, Array.from(cell.parentElement.cells), sizes);
          });
        }
      }

      addGroup(labelCell, cells, sizes) {
        const key = this.normalize(labelCell.textContent);
        const value = sizes.get(key);

        // Keep the cell's own content and make it the button's content.
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'size-guide-modal__size';
        while (labelCell.firstChild) button.appendChild(labelCell.firstChild);
        labelCell.appendChild(button);
        button.addEventListener('click', () => {
          this.lastSizeButton = button;
          this.selectSize(value);
        });

        const group = this.groups.get(key) || { cells: [], buttons: [] };
        group.cells.push(...cells);
        group.buttons.push(button);
        this.groups.set(key, group);
      }

      highlightSelectedSize() {
        const value = this.selectedSizeValue();
        this.highlight(value ? this.normalize(value) : null);
      }

      highlight(selectedKey) {
        this.groups.forEach(({ cells, buttons }, key) => {
          const selected = key === selectedKey;
          cells.forEach((cell) => cell.classList.toggle('is-selected', selected));
          buttons.forEach((button) => button.setAttribute('aria-pressed', String(selected)));
        });
      }
    }
  );
}
